import { DragDropContext, type DropResult } from "@hello-pangea/dnd"
import { Alert, Card, Col, Descriptions, Form, Row, Transfer } from "antd"
import { useEffect, useState } from "react"

import Flex from "#blocks/Flex"
import { type ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { type Uuid } from "#customTypes/globalTypes"

import { useCurriculumFormContext } from "./AddCurriculumFormContext"
import ProjectTimeline from "./ProjectTimeline"

import type { TransferProps } from "antd/es/transfer"

const calculateTotalDays = (
  projectIds: Uuid[],
  projectsById: Record<Uuid, ProjectConstructor>
) => {
  return projectIds.reduce(
    (sum, id) => sum + parseInt(projectsById[id]?.daysToComplete ?? "0"),
    0
  )
}

const ProjectAssignmentForm = () => {
  const [totalDays, setTotalDays] = useState(0)
  const [selectedProjectId, setSelectedProjectId] = useState<Uuid | null>(null)
  const form = Form.useFormInstance()

  const numberOfSessions = Form.useWatch("numberOfSessions", form)

  const {
    projectsById,
    projectOptions,
    allProjects,
    setProjectIds,
    projectIds,
  } = useCurriculumFormContext()

  // Derive the selected project from the ID
  const selectedProject = selectedProjectId
    ? projectsById[selectedProjectId]
    : null

  useEffect(() => {
    if (!selectedProjectId && projectIds?.length) {
      setSelectedProjectId(projectIds[0])
    }
  }, [selectedProjectId, projectIds])

  // Update calculations when project order changes
  useEffect(() => {
    if (!projectIds?.length) return

    setTotalDays(calculateTotalDays(projectIds, projectsById))

    // Clear selected project if it was removed from the list
    if (selectedProjectId && !projectIds.includes(selectedProjectId)) {
      setSelectedProjectId(null)
    }
  }, [projectIds, projectsById, selectedProjectId])

  const handleProjectOrderChange = (result: DropResult) => {
    if (!result.destination) return
    if (result.source.index === result.destination.index) return

    const reorderedIds = Array.from(projectIds)
    const [removed] = reorderedIds.splice(result.source.index, 1)
    reorderedIds.splice(result.destination.index, 0, removed)

    setProjectIds(reorderedIds)
  }

  const handleListTransfer: TransferProps["onChange"] = (newProjectIds) => {
    const newIdSet = new Set(newProjectIds as string[])
    const existingIdSet = new Set(projectIds)

    // Preserve existing order for items that are still selected
    const existingInOrder = projectIds.filter((id) => newIdSet.has(id))

    // For newly added items, use projectOptions order (not Transfer's alphabetical order)
    const newlyAdded = projectOptions
      .filter((opt) => newIdSet.has(opt.key) && !existingIdSet.has(opt.key))
      .map((opt) => opt.key as Uuid)

    const mergedIds = [...existingInOrder, ...newlyAdded]

    setProjectIds(mergedIds)
    // Sync to form so context can load the correct projects via useSomeDocs
    // form.setFieldValue("projectIds", mergedIds)
  }

  const renderProjectDetails = () => {
    if (!numberOfSessions) return null

    // Show error if a project ID is selected but the project data is missing
    if (selectedProjectId && !selectedProject) {
      return (
        <Alert
          type="error"
          message="Project Failed to Load"
          description={`Project ID: ${selectedProjectId}. This project may have been deleted or is from a different semester.`}
          style={{ marginTop: 16 }}
        />
      )
    }

    if (!selectedProject) return null

    return (
      <Descriptions
        title="Project Details"
        bordered
        size="small"
        column={1}
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="Name">
          {selectedProject.name}
        </Descriptions.Item>
        <Descriptions.Item label="Days to Complete">
          {selectedProject.daysToComplete}
        </Descriptions.Item>
        <Descriptions.Item label="Medium">
          {selectedProject.medium}
        </Descriptions.Item>
        <Descriptions.Item label="Skill Levels">
          {selectedProject.skillLevels?.join(", ")}
        </Descriptions.Item>
        {selectedProject.description && (
          <Descriptions.Item label="Description">
            {selectedProject.description}
          </Descriptions.Item>
        )}
        {selectedProject.notes && (
          <Descriptions.Item label="Notes">
            {selectedProject.notes}
          </Descriptions.Item>
        )}
        {selectedProject.link && (
          <Descriptions.Item label="Link">
            <a
              href={selectedProject.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {selectedProject.link}
            </a>
          </Descriptions.Item>
        )}
      </Descriptions>
    )
  }

  const getStatusMessage = () => {
    if (!numberOfSessions)
      return (
        <Alert type="warning" message="Please fill in the number of sessions" />
      )

    const diff = numberOfSessions - totalDays
    if (diff === 0) {
      return (
        <Alert
          type="success"
          message="Project days match curriculum sessions perfectly!"
        />
      )
    } else if (diff > 0) {
      return (
        <Alert
          type="warning"
          message={`Need ${diff} more days of projects to fill curriculum`}
        />
      )
    }
    return (
      <Alert
        type="error"
        message={`Projects exceed curriculum by ${Math.abs(diff)} days`}
      />
    )
  }

  return (
    <Flex col gap={16}>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="projectIds"
            label="Select and Order Projects"
            rules={[{ required: false }]}
          >
            <Transfer
              dataSource={projectOptions}
              showSearch
              filterOption={(input, option) =>
                option?.title.toLowerCase().includes(input.toLowerCase()) ||
                option?.description
                  .toLowerCase()
                  .includes("(" + input.toLowerCase())
              }
              style={{ maxWidth: 1180 }}
              listStyle={{
                width: "100%",
                height: 300,
              }}
              operations={["Select", "Remove"]}
              targetKeys={projectIds}
              render={(item) => (
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 470,
                  }}
                >
                  {item.title} {item.description}
                </div>
              )}
              oneWay
              onChange={handleListTransfer}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Card title="Project Timeline" style={{ width: "100%" }}>
            {!allProjects?.length && (
              <Alert type="info" message="Add projects to see them here." />
            )}
            {allProjects?.length && (
              <Flex col gap={16}>
                <Flex>{getStatusMessage()}</Flex>
                <DragDropContext onDragEnd={handleProjectOrderChange}>
                  <ProjectTimeline
                    numberOfSessions={numberOfSessions}
                    totalDays={totalDays}
                    selectedProjectId={selectedProjectId}
                    setSelectedProjectId={setSelectedProjectId}
                  />
                </DragDropContext>
                {renderProjectDetails()}
              </Flex>
            )}
          </Card>
        </Col>
      </Row>
    </Flex>
  )
}

export default ProjectAssignmentForm
